import { ChangeDetectionStrategy, Component, ComponentRef, Input } from '@angular/core';
import { LCLShipmentModel, QuoteManualDetailedModel, QuoteModel } from '@data-layer/models/lcl';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';
import { AppService } from '@app/app.service';
import { QuoteRepository, ShipmentRepository } from '@data-layer/repositories';
import { switchMap, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { LclShipmentService } from '@app/modules/lcl-shipping/services/lcl-shipment.service';
import { enumDetailedInfoPlacementEnum, enumShipmentState } from '@data-layer/enums/lcl';
import { enumLclTrackingStates } from '@enum/enumLclTrackingStates';
import { LclEditIncotermsComponent } from '../lcl-edit-incoterms/lcl-edit-incoterms.component';
import { LclQuoteService } from '@app/modules/lcl-shipping/services';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngxs/store';
import { OpenLCLMarineInsuranceQuoteSidebar } from '@app/modules/lcl-shipping/state';
import { MarineInsuranceAvailabilityService } from '@shared/marine-insurance-shared/services';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import { enumLclInsuranceBlockReason } from '@data-layer/enums/marine-insurance';
import { enumMarineInsuranceStatusType } from '@app/data-layer/enums/marine-insurance/enumInsuranceStatusType.enum';

@Component({
  selector: 'app-lcl-header-detailed-info',
  templateUrl: './lcl-header-detailed-info.component.html',
  styleUrls: ['./lcl-header-detailed-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclHeaderDetailedInfoComponent {
  private static readonly INSURANCE_BLOCK_TOOLTIP_KEYS: Record<enumLclInsuranceBlockReason, string> = {
    [enumLclInsuranceBlockReason.TRACKING_STATE]: 'LABELS.INSURANCE_UNAVAILABLE_DELIVERED',
    [enumLclInsuranceBlockReason.SAILING_WINDOW]: 'LABELS.INSURANCE_UNAVAILABLE_SAILING_WINDOW',
  };

  @Input() public info: QuoteModel | LCLShipmentModel | QuoteManualDetailedModel;
  @Input() public referenceName: string;
  @Input() public referenceValue: string;
  @Input() public isDashboardPlacement: boolean;
  @Input() public isHideIncoterms: boolean;
  @Input() public isHideEditIncoterms: boolean;
  @Input() public isQuote: boolean;
  @Input() public isManualQuote: boolean;
  @Input() public template: number;
  /** Opt-in: only the client shipment header binds it, so the shield never renders on hosts that cannot open the quote sidebar. */
  @Input() public isShowInsuranceIcon: boolean = false;

  public enumShipmentState: typeof enumShipmentState = enumShipmentState;
  public enumLclTrackingStates: typeof enumLclTrackingStates = enumLclTrackingStates;
  public enumIncotermsType: typeof enumIncotermsType = enumIncotermsType;
  public enumDetailedInfoPlacementEnum: typeof enumDetailedInfoPlacementEnum = enumDetailedInfoPlacementEnum;
  public enumCompanyPlacement: typeof enumCompanyPlacement = enumCompanyPlacement;

  constructor(
    private appService: AppService,
    private shipmentRepository: ShipmentRepository,
    private lclShipmentService: LclShipmentService,
    private quoteRepository: QuoteRepository,
    private lclQuoteService: LclQuoteService,
    private store: Store,
    private marineInsuranceAvailabilityService: MarineInsuranceAvailabilityService,
    public sanitizer: DomSanitizer,
  ) {}

  public onEditIncoTerms(): void {
    const componentSidebarRef: ComponentRef<LclEditIncotermsComponent> = this.appService.openSidebar(LclEditIncotermsComponent, {
      sidebarHeaderText: 'PORT_CHARGES',
      tooltipText: 'LABELS.INCOTERMS_HINT',
    });

    componentSidebarRef.instance.shipment = this.info as LCLShipmentModel;
    componentSidebarRef.instance.isHideRoleSelector = !this.isQuote;
    componentSidebarRef.instance.dataSaved
      .pipe(
        take(1),
        switchMap((res: { exportLicenseRequested: boolean; incoTermsType: enumIncotermsType }) => this.updateIncoTerms(res)),
        switchMap(() => (this.isQuote ? this.lclQuoteService.cleanOldEntities() : of(true))),
      )
      .subscribe(() => {
        if (this.isQuote) {
          this.lclQuoteService.updateLclQuote$.next();
        } else {
          this.lclShipmentService.updateLclShipment$.next();
        }

        this.appService.closeSidebar();
      });
  }

  public get shipment(): LCLShipmentModel {
    return this.info as LCLShipmentModel;
  }

  public get isShowInsuranceIndicator(): boolean {
    if (!this.isShowInsuranceIcon || this.isQuote || !this.shipment) {
      return false;
    }

    return this.marineInsuranceAvailabilityService.isShowIndicatorForLclShipment(this.shipment);
  }

  public get insuranceBlockReason(): enumLclInsuranceBlockReason | null {
    // an issued policy stays reachable (certificate download) regardless of tracking state or dates
    if (this.shipment.insuranceStatus === enumMarineInsuranceStatusType.INSURED) {
      return null;
    }

    return this.marineInsuranceAvailabilityService.getLclShipmentInsuranceBlockReason(this.shipment);
  }

  public get isInsuranceBlocked(): boolean {
    return this.insuranceBlockReason !== null;
  }

  public get insuranceBlockTooltipKey(): string | null {
    const reason: enumLclInsuranceBlockReason | null = this.insuranceBlockReason;

    return reason === null ? null : LclHeaderDetailedInfoComponent.INSURANCE_BLOCK_TOOLTIP_KEYS[reason];
  }

  public onAddInsurance(): void {
    // the header lives in the app layout, not on the shipment page, so it opens the sidebar itself via the store
    this.store.dispatch(new OpenLCLMarineInsuranceQuoteSidebar({ shipment: this.shipment }));
  }

  public get isShowIncoterms(): boolean {
    if (!!this.isHideIncoterms) {
      return false;
    }

    if (this.isQuote) {
      return !this.isDashboardPlacement;
    }

    return true;
  }

  public get isShowEditIncoterms(): boolean {
    if (this.isDashboardPlacement || this.isHideEditIncoterms) {
      return false;
    }

    const state: enumShipmentState = (this.info as LCLShipmentModel)?.state;
    const trackingState: enumLclTrackingStates = (this.info as LCLShipmentModel)?.trackingState;

    return this.isQuote ? true : state !== enumShipmentState.CANCELED && trackingState < enumLclTrackingStates.AT_ORIGIN_WAREHOUSE;
  }

  public get fromPortHtmlLabel(): string {
    // due to the view encapsulation - style attr is here
    if (this.isManualQuote) {
      return `<strong style="font-weight: 600;">${(this.info as QuoteManualDetailedModel)?.departurePort?.displayName}</strong> (${
        (this.info as QuoteManualDetailedModel)?.departurePort?.unCode
      }), ${(this.info as QuoteManualDetailedModel)?.departurePort?.country?.description}`;
    } else {
      return `<strong style="font-weight: 600;">${(this.info as QuoteModel)?.voyage?.route?.departurePort?.displayName}</strong> (${
        (this.info as QuoteModel)?.voyage?.route?.departurePort?.unCode
      }), ${(this.info as QuoteModel)?.voyage?.route?.departurePort?.country?.description}`;
    }
  }

  public get toPortHtmlLabel(): string {
    if (this.isManualQuote) {
      return `<strong style="font-weight: 600;">${(this.info as QuoteManualDetailedModel)?.arrivalPort?.displayName}</strong> (${
        (this.info as QuoteManualDetailedModel)?.arrivalPort?.unCode
      }), ${(this.info as QuoteManualDetailedModel)?.arrivalPort?.country?.description}`;
    } else {
      return `<strong style="font-weight: 600;">${(this.info as QuoteModel)?.voyage?.route?.arrivalPort?.displayName}</strong> (${
        (this.info as QuoteModel)?.voyage?.route?.arrivalPort?.unCode
      }), ${(this.info as QuoteModel)?.voyage?.route?.arrivalPort?.country?.description}`;
    }
  }

  public get fromPortLabel(): string {
    // due to the view encapsulation - style attr is here
    if (this.isManualQuote) {
      return `${(this.info as QuoteManualDetailedModel)?.departurePort?.displayName} (${
        (this.info as QuoteManualDetailedModel)?.departurePort?.unCode
      }), ${(this.info as QuoteManualDetailedModel)?.departurePort?.country?.description}`;
    } else {
      return `${(this.info as QuoteModel)?.voyage?.route?.departurePort?.displayName} (${
        (this.info as QuoteModel)?.voyage?.route?.departurePort?.unCode
      }), ${(this.info as QuoteModel)?.voyage?.route?.departurePort?.country?.description}`;
    }
  }

  public get toPortLabel(): string {
    if (this.isManualQuote) {
      return `${(this.info as QuoteManualDetailedModel)?.arrivalPort?.displayName} (${
        (this.info as QuoteManualDetailedModel)?.arrivalPort?.unCode
      }), ${(this.info as QuoteManualDetailedModel)?.arrivalPort?.country?.description}`;
    } else {
      return `${(this.info as QuoteModel)?.voyage?.route?.arrivalPort?.displayName} (${
        (this.info as QuoteModel)?.voyage?.route?.arrivalPort?.unCode
      }), ${(this.info as QuoteModel)?.voyage?.route?.arrivalPort?.country?.description}`;
    }
  }

  private updateIncoTerms(updatedData: { exportLicenseRequested: boolean; incoTermsType: enumIncotermsType }): Observable<any> {
    return this.isQuote
      ? this.quoteRepository.update({ ...updatedData })
      : this.shipmentRepository.updateIncoterms(this.info.id, updatedData);
  }
}
